<template>
  <v-container>
    <v-card class="pa-6 elevation-2">
      <!-- Header Section -->
      <v-row class="mb-4 align-center justify-space-between">
        <v-col cols="auto">
          <h2 class="text-h5 font-weight-bold mb-2">編輯工具資訊</h2>
          <p class="text-subtitle-2 grey--text">請編輯工具詳細資訊，完成後保存變更。</p>
        </v-col>
        <v-col cols="auto">
          <v-btn color="secondary" outlined @click="goBack">
            返回
          </v-btn>
        </v-col>
      </v-row>

      <!-- Tool Editing Cards -->
      <v-row>
        <!-- Basic Information Card -->
        <v-col cols="12" md="6">
          <v-card class="pa-4 mb-4 elevation-1">
            <v-card-title class="text-h6 font-weight-bold">基本資訊</v-card-title>
            <v-divider class="mb-3"></v-divider>
            <v-simple-table dense>
              <tbody>
                <tr v-for="fieldName in basicFields" :key="fieldName">
                  <td class="font-weight-medium" style="width: 40%;">{{ fieldName }}</td>
                  <td>
                    <!-- 靜態顯示 -->
                    <template v-if="fieldName === '名稱'">
                      <span>{{ tool[getFieldKey(fieldName)] || '' }}</span>
                    </template>

                    <!-- 可編輯欄位 -->
                    <template v-else-if="editableFields.includes(fieldName)">
                      <v-text-field v-model="tool[getFieldKey(fieldName)]" variant="outlined" dense color="primary"
                        class="full-width" hide-details></v-text-field>
                    </template>

                    <!-- 靜態顯示其他欄位 -->
                    <template v-else>
                      <template v-if="Array.isArray(tool[getFieldKey(fieldName)])">
                        <ul>
                          <li v-for="item in tool[getFieldKey(fieldName)]" :key="item">{{ item }}</li>
                        </ul>
                      </template>
                      <template v-else-if="fieldName === '工具圖片連結'">
                        <v-img :src="tool[getFieldKey(fieldName)]" max-width="100" max-height="100"></v-img>
                      </template>
                      <template v-else>
                        <span>{{ tool[getFieldKey(fieldName)] || '' }}</span>
                      </template>
                    </template>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

          </v-card>
        </v-col>

        <!-- Technical Information Card -->
        <v-col cols="12" md="6">
          <v-card class="pa-4 mb-4 elevation-1">
            <v-card-title class="text-h6 font-weight-bold">技術細節</v-card-title>
            <v-divider class="mb-3"></v-divider>
            <v-simple-table dense>
              <tbody>
                <tr v-for="fieldName in technicalFields" :key="fieldName">
                  <td class="font-weight-medium" style="width: 40%;">{{ fieldName }}</td>
                  <td>
                    <template v-if="editableFields.includes(fieldName)">
                      <v-select v-if="fieldName === '是否開源'" v-model="tool[getFieldKey(fieldName)]"
                        :items="openSourceOptions" label="是否開源" outlined dense hide-details></v-select>
                      <v-select v-else-if="fieldName === '工具大分類'" v-model="tool[getFieldKey(fieldName)]"
                        :items="classtypeOptions" label="工具大分類" outlined dense hide-details></v-select>
                      <v-text-field v-else v-model="tool[getFieldKey(fieldName)]" variant="outlined" dense
                        color="primary" class="editable-field modern-input" hide-details></v-text-field>
                    </template>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Usage and Assessment Card -->
      <v-row>
        <v-col cols="12">
          <v-card class="pa-4 mb-4 elevation-1">
            <v-card-title class="text-h6 font-weight-bold">使用與評估</v-card-title>
            <v-divider class="mb-3"></v-divider>
            <v-simple-table dense>
              <tbody>
                <tr v-for="fieldName in usageAndAssessmentFields" :key="fieldName">
                  <td class="font-weight-medium" style="width: 45%;">{{ fieldName }}</td>
                  <td>
                    <template v-if="editableFields.includes(fieldName)">
                      <v-text-field v-model="tool[getFieldKey(fieldName)]" variant="outlined" dense color="primary"
                        class="editable-field modern-input" hide-details></v-text-field>
                    </template>
                    <template v-else>
                      <span>{{ tool[getFieldKey(fieldName)] || '' }}</span>
                    </template>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Additional Information Card -->
      <v-row>
        <v-col cols="12">
          <v-card class="pa-4 mb-4 elevation-1">
            <v-card-title class="text-h6 font-weight-bold">其他細節</v-card-title>
            <v-divider class="mb-3"></v-divider>
            <v-simple-table dense>
              <colgroup>
                <col style="width: 15%;">
                <col style="width: 85%;">
              </colgroup>
              <tbody>
                <tr v-for="fieldName in additionalFields" :key="fieldName">
                  <td class="font-weight-medium narrow-column">{{ fieldName }}</td>
                  <td>
                    <template v-if="editableFields.includes(fieldName)">
                      <!-- 應用場景 -->
                      <v-select v-if="fieldName === '應用場景'" v-model="tool[getFieldKey(fieldName)]"
                        :items="useCaseOptions" label="應用場景" multiple outlined dense filterable class="full-width"
                        :menu-props="{ closeOnContentClick: false, closeOnClick: false }"
                        v-model:search-input="searchInput" @update:modelValue="onUpdateUseCase" />
                      <!-- 語系 -->
                      <v-select v-else-if="fieldName === '語系'" v-model="tool[getFieldKey(fieldName)]"
                        :items="languageOptions" label="語系" multiple outlined dense class="full-width" />
                      <!-- 費用 -->
                      <v-select v-else-if="fieldName === '費用'" v-model="tool[getFieldKey(fieldName)]"
                        :items="costOptions" label="費用" multiple outlined dense class="full-width" />
                      <!-- 其他 -->
                      <v-text-field v-else v-model="tool[getFieldKey(fieldName)]" variant="outlined" dense
                        color="primary" class="editable-field modern-input full-width" hide-details />
                    </template>
                    <!-- 靜態顯示 -->
                    <template v-else>
                      <template v-if="Array.isArray(tool[getFieldKey(fieldName)])">
                        <ul>
                          <li v-for="item in tool[getFieldKey(fieldName)]" :key="item">{{ item }}</li>
                        </ul>
                      </template>
                      <template v-else-if="fieldName === '工具圖片連結'">
                        <v-img :src="tool[getFieldKey(fieldName)]" max-width="100" max-height="100"></v-img>
                      </template>
                      <template v-else>
                        <span>{{ tool[getFieldKey(fieldName)] || '' }}</span>
                      </template>
                    </template>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>


      <!-- 操作按鈕 -->
      <v-row justify="end" class="mt-4">
        <v-btn color="primary" @click="saveChanges">
          <v-icon left>mdi-content-save</v-icon> 確認
        </v-btn>
        <v-btn color="secondary" outlined class="ml-2" @click="cancelEdit">
          <v-icon left>mdi-cancel</v-icon> 取消
        </v-btn>
      </v-row>
    </v-card>
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>

  </v-container>
</template>


<script>
import axios from "axios";

export default {
  name: "EditTool",
  data() {
    return {
      datePickerMenu: false,
      useCaseOptions: [], // 用於存儲應用場景選項
      searchInput: "", // 新增搜索輸入值
      tool: {

      },
      fieldMappings: {
      },
      openSourceOptions: [
        "開源",
        "閉源",
      ],
      classtypeOptions: [
        "應用工具",
        "開發工具",
        "語言模型",
      ],
      languageOptions: [
        "英文",
        "繁體中文",
        "其他",
      ],
      costOptions: [
        "免費",
        "訂閱",
        "買斷",
        "計次",
        "專人洽詢",
      ],
      editableFields: [
        "所屬公司",
        "功能描述",
        "工具圖片連結",
        "是否開源",
        "工具大分類",
        "官方網址",
        "入門指南",
        "費用",
        "應用場景",
        "語系",
        "產品國籍",
        "程式語言",
        "最新版本日期",
        "風險評估",
        "主題",
        "使用說明",
        "技術需求",
        "使用模型",
        "使用介面",
      ],
      snackbar: {
        show: false,
        text: "",
        color: "",
      },

    };
  },
  created() {
    const codeno = this.$route.params.codeno;
    if (codeno) {
      this.fetchToolDetails(codeno);
    } else {
      console.error("未獲取到有效的 codeno");
    }
    this.fetchUseCaseOptions(); // 加載應用場景選項
  },
  computed: {

    basicFields() {
      return ["名稱", "完整名稱", "所屬公司", "官方網址", "產品國籍", "工具圖片連結"];
    },
    technicalFields() {
      return ["程式語言","使用說明", "技術需求", "使用模型", "使用介面", "是否開源", "工具大分類"];
    },
    usageAndAssessmentFields() {
      return ["推薦度", "使用者喜愛度", "Hugging Face 受歡迎度", "GitHub 受歡迎度", "風險評估"];
    },

    additionalFields() {
      return ["功能描述", "入門指南", "語系", "應用場景", "最新版本日期", "費用"];
    }
  },
  methods: {
    onUpdateUseCase(newValue) {
      const maxSelections = 10;
      if (newValue.length > maxSelections) {
        // 顯示警告
        this.snackbar = {
          show: true,
          text: `最多只能選擇 ${maxSelections} 個應用場景！`,
          color: "red",
        };
        // 限制選擇數量
        this.tool["應用場景"] = newValue.slice(0, maxSelections);
      } else {
        this.tool["應用場景"] = newValue;
      }
    },

    async fetchUseCaseOptions() {
      try {
        const baseUrlo = process.env.VUE_APP_SERVICE_URL;
        const baseUrl = process.env.VUE_APP_BASE_URL || `${baseUrlo}/api/v1`;
        const response = await axios.get(`${baseUrl}/UseCaseList`);
        if (response.data.rtnCode === "0000") {
          this.useCaseOptions = [...new Set(response.data.data.map((item) => item.name))];
          console.log("應用場景選項加載成功:", this.useCaseOptions);
        } else {
          console.error("無法獲取應用場景選項:", response.data.rtnMsg);
        }
      } catch (error) {
        console.error("應用場景 API 請求失敗:", error);
      }
    }
    ,
    async fetchToolDetails(codeno) {
      try {
        const baseUrlo = process.env.VUE_APP_SERVICE_URL;
        const baseUrl = process.env.VUE_APP_BASE_URL || `${baseUrlo}/api/v1`;
        const response = await axios.get(`${baseUrl}/ToolsDetail/${codeno}`);
        if (response.data.rtnCode === "0000") {
          this.tool = {
            ...response.data.data,
            語系: Array.isArray(response.data.data["語系"])
              ? response.data.data["語系"]
              : [],
            費用: Array.isArray(response.data.data["費用"])
              ? response.data.data["費用"]
              : [],
            應用場景: response.data.data["應用場景"]
              ? response.data.data["應用場景"].split(",").map((item) => item.trim())
              : [],
          };
          console.log("工具詳細資料:", this.tool);
        } else {
          console.error("無法獲取工具詳細資料:", response.data.rtnMsg);
        }
      } catch (error) {
        console.error("工具詳細資料請求失敗:", error);
      }
    },

    async saveChanges() {
      try {
        console.log("提交的資料:", this.tool); // 檢查原始資料
        const baseUrlo = process.env.VUE_APP_SERVICE_URL;
        const baseUrl = process.env.VUE_APP_BASE_URL || `${baseUrlo}/api/v1`;
        const formatDate = (date) => {
          if (!date) return null; // 如果日期為空，返回 null
          const parsedDate = new Date(date);
          if (isNaN(parsedDate.getTime())) return null; // 如果日期無效，返回 null
          return parsedDate.toISOString(); // 格式化為 ISO 8601 格式
        };

        // 格式化提交的資料
        const formattedData = {
          名稱: this.tool["名稱"] || "",
          完整名稱: this.tool["完整名稱"] || "",
          所屬公司: this.tool["所屬公司"] || "",
          功能描述: this.tool["功能描述"] || "",
          工具圖片連結: this.tool["工具圖片連結"] || "",
          是否開源: this.tool["是否開源"] || "",
          工具大分類: this.tool["工具大分類"] || "",
          官方網址: this.tool["官方網址"] || "",
          入門指南: Array.isArray(this.tool["入門指南"]) ? this.tool["入門指南"].join(", ") : this.tool["入門指南"] || "",
          語系: Array.isArray(this.tool["語系"]) ? this.tool["語系"] : [],
          費用: Array.isArray(this.tool["費用"]) ? this.tool["費用"] : [],
          產品國籍: this.tool["產品國籍"] || "",
          程式語言: this.tool["程式語言"] || "",
          最新版本日期: formatDate(this.tool["最新版本日期"]),
          風險評估: this.tool["風險評估"] || "",
          主題: Array.isArray(this.tool["主題"]) ? this.tool["主題"] : [],
          使用說明: this.tool["使用說明"] || "",
          技術需求: this.tool["技術需求"] || "",
          使用模型: this.tool["使用模型"] || "",
          使用介面: this.tool["使用介面"] || "",
          應用場景: typeof this.tool["應用場景"] === "string"
            ? this.tool["應用場景"].split(",").map((item) => item.trim())
            : Array.isArray(this.tool["應用場景"])
              ? this.tool["應用場景"]
              : [],
        };


        console.log("格式化後的資料:", formattedData);

        // 發送 API 請求
        const response = await axios.post(`${baseUrl}/ToolsEdit/${this.tool.Codeno}`, formattedData, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        // 處理 API 返回
        if (response.data.rtnCode === "0000") {
          console.log("工具更新成功:", response.data);
          this.$router.push({ name: "ViewTool", params: { codeno: this.tool.Codeno } });
        } else {
          console.error("工具更新失敗:", response.data.rtnMsg);
        }
      } catch (error) {
        console.error("保存工具資料失敗:", error.response ? error.response.data : error.message);
      }
    }
    ,
    cancelEdit() {
      this.$router.push({ name: "ViewTool", params: { codeno: this.tool.Codeno } });
    },
    goBack() {
      this.$router.push({ name: "tools" });
    },

    getFieldKey(fieldName) {
      // 支援多語欄位名稱對應
      return Object.keys(this.fieldMappings).find((key) => this.fieldMappings[key] === fieldName) || fieldName;
    },


    isDateField(fieldName) {
      return ["建立時間", "審核時間", "上架時間", "停用時間", "異動時間", "最新版本日期"].includes(fieldName);
    },
  },
};
</script>

<style scoped>
.custom-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

th {
  background-color: #3f51b5;
  color: white;
  font-weight: bold;
  text-align: left;
  padding: 10px;
}

td {
  padding: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.full-width {
  width: 100%;
  box-sizing: border-box;
  /* 确保内边距不会影响宽度 */
}

.editable-field {
  width: 100%;
  min-height: 32px;
  /* 調整高度 */
  font-size: 14px;
  /* 增加文字大小 */
}

.mt-4 {
  margin-top: 16px;
}

.mb-4 {
  margin-bottom: 16px;
}

td {
  width: 100%;
}

v-simple-table {
  width: 100%;
}

/* 自定義寬度，避免過窄 */
.v-text-field,
.v-select {
  min-width: 250px;
  max-width: 400px;
}

.v-text-field.v-input--solo {
  background-color: #ffffff;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.v-text-field.v-input--solo:focus-within {
  border-color: #3f51b5;
  box-shadow: 0 2px 6px rgba(63, 81, 181, 0.2);
}

.v-select[data-label="應用場景"] {
  min-height: 120px;
  /* 預留更多空間 */
}

.full-width {
  width: 100%;
}

.v-text-field,
.v-select {
  width: 100%;
  max-width: none;
  /* 確保寬度不受限制 */
}

.v-select__control {
  min-height: 48px;
  /* 根據需要調整高度 */
}

td {
  vertical-align: middle;
}

th,
td:first-of-type {
  white-space: nowrap;
}

.narrow-column {
  width: 15%;
  /* 確保欄名更窄 */
  max-width: 100px;
  /* 設定最大寬度 */
  text-overflow: ellipsis;
  /* 長文字顯示省略號 */
  overflow: hidden;
  /* 隱藏多餘內容 */
  white-space: nowrap;
  /* 禁止文字換行 */
}

td:first-of-type {
  width: 15%;
  /* 固定第一列寬度 */
  max-width: 100px;
  /* 限制最大寬度 */
  white-space: nowrap;
  /* 禁止換行 */
  overflow: hidden;
  /* 隱藏溢出內容 */
  text-overflow: ellipsis;
  /* 長文字顯示省略號 */
}
</style>
